
import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { get, UseStore } from 'idb-keyval';
import { Observable, from, merge, of } from 'rxjs';
import { catchError, map, mergeMap, take, tap, timeout } from 'rxjs/operators';
import { rehydrateAction, rehydrateErrorAction, rehydrateInitAction } from './ngrx-store-idb.actions';
import { NgrxStoreIdbStoreProvider } from './ngrx-store-idb-store.provider';
import { NgrxStoreIdbTenantResolver } from './ngrx-store-idb-tenant-resolver';
import { NgrxStoreIdbOptions, OPTIONS, SAVED_STATE_KEY } from './ngrx-store-idb.options';

@Injectable({
  providedIn: 'root',
})
export class RehydrateEffects implements OnInitEffects {
  private actions$ = inject(Actions);
  private options = inject<NgrxStoreIdbOptions>(OPTIONS);
  private storeProvider = inject(NgrxStoreIdbStoreProvider);
  private tenantResolver = inject(NgrxStoreIdbTenantResolver);

  // State read from IDB
  rehydratedState: unknown;

  /**
   * This effect is triggered after root store initialisation.
   * This is the very first thing done by NgRx and the best place
   * to load saved data.
   *
   * When the tenant (and therefore the store) is not known yet, an EMPTY
   * rehydrate is dispatched immediately: application startup must not wait
   * for the store, because on a fresh visit the tenant only resolves
   * through the auth flow, and the auth flow itself only starts once a
   * rehydrate outcome has been dispatched. The real rehydrate then runs as
   * soon as initTenantStore$ creates the store.
   */
  rehydrateOnInit$ = createEffect(() =>
    { return this.actions$.pipe(
      ofType(rehydrateInitAction),
      mergeMap(() => {
        const deferredRehydrate$ = this.storeProvider.store$.pipe(
          take(1),
          mergeMap(idbStore => this.loadSavedState(idbStore)),
        );
        if (this.storeProvider.getIfReady() == null) {
          return merge(
            of(rehydrateAction({ rehydratedState: null })),
            deferredRehydrate$,
          );
        }
        return deferredRehydrate$;
      }),
    ); },
  );

  /**
   * Once the application resolves the tenant, initialise the tenant store
   * if bootstrap couldn't (fresh visit), or reload if the bootstrap tenant
   * turned out to be the wrong one (e.g. another user logged in on this tab).
   */
  initTenantStore$ = createEffect(
    () =>
      { return this.tenantResolver.tenantId$().pipe(
        take(1),
        tap((tenantId) => {
          if (this.storeProvider.tenantIdInUse == null) {
            this.storeProvider.init(tenantId);
          } else if (this.storeProvider.tenantIdInUse !== tenantId) {
            this.tenantResolver.onTenantMismatch(tenantId);
          }
        }),
      ); },
    { dispatch: false },
  );

  private loadSavedState(idbStore: UseStore): Observable<Action> {
    if (this.options.debugInfo) {
      // This is only enabled if options.debugInfo is set to true
      // eslint-disable-next-line no-console
      console.debug('NgrxStoreIdb: Load state from IndexedDB');
    }
    return from(get(SAVED_STATE_KEY, idbStore)).pipe(
      // The read can queue indefinitely when a deleteDatabase is pending in
      // another tab; degrade to a rehydrate error instead of hanging startup
      // (the auth flow waits on a rehydrate outcome).
      timeout(this.options.concurrency?.refreshRate ?? 5000),
      tap(value => (this.rehydratedState = value)),
      tap((value) => {
        if (this.options.debugInfo) {
          // This is only enabled if options.debugInfo is set to true
          // eslint-disable-next-line no-console
          console.debug('NgrxStoreIdb: Loaded state from IndexedDB:', value);
        }
      }),
      map(value => rehydrateAction({ rehydratedState: value })),
      catchError((err) => {
        console.error('NgrxStoreIdb: Error reading state from IndexedDB', err);
        return of(rehydrateErrorAction());
      }),
    );
  }

  ngrxOnInitEffects(): Action {
    if (this.options.rehydrate) {
      return rehydrateInitAction();
    }
  }
}
